import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useInView, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { Users, PiggyBank, Target, Award, Heart, DollarSign, Trophy, Rocket, Star, ChevronDown, Shield } from 'lucide-react';
import './AboutUs.css';

// Organic blob backgrounds for visual depth
const OrganicBlobs = () => (
  <div className="aboutus-organic-blobs">
    <div className="aboutus-organic-blob blob-1"></div>
    <div className="aboutus-organic-blob blob-2"></div>
    <div className="aboutus-organic-blob blob-3"></div>
    <div className="aboutus-organic-blob blob-4"></div>
  </div>
);

// Enhanced floating particles with varied colors and sizes
const EnhancedParticles = () => (
  <div className="aboutus-enhanced-particles">
    {[...Array(20)].map((_, i) => (
      <div 
        key={i} 
        className={`aboutus-enhanced-particle particle-${i % 6}`}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          width: `${Math.random() * 8 + 2}px`,
          height: `${Math.random() * 8 + 2}px`,
          animationDelay: `${Math.random() * 10}s`,
          animationDuration: `${Math.random() * 15 + 10}s`
        }}
      />
    ))}
  </div>
);

// Enhanced mission statement with interactive elements
const EnhancedMissionStatement = () => {
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.5 });
  
  return (
    <motion.div 
      ref={ref}
      className="aboutus-mission-container"
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      <div className="aboutus-mission-background ultra-glass">
        <div className="aboutus-mission-shine"></div>
        <div className="aboutus-mission-noise"></div>
      </div>
      
      <div className="aboutus-mission-content">
        <motion.div 
          className="aboutus-mission-icon-container"
          onHoverStart={() => setIsHovered(true)}
          onHoverEnd={() => setIsHovered(false)}
          animate={{
            scale: isHovered ? 1.1 : 1,
            rotate: isHovered ? 10 : 0
          }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <Target size={64} className="aboutus-mission-icon" />
          <motion.div 
            className="aboutus-icon-pulse" 
            animate={{
              scale: isHovered ? [1, 1.5, 1] : [1, 1.2, 1],
              opacity: isHovered ? [0.2, 0.5, 0.2] : [0.2, 0.3, 0.2]
            }}
            transition={{ 
              duration: isHovered ? 1.2 : 2, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
          />
        </motion.div>
        
        <motion.h2 
          className="aboutus-section-title mission-title"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <span className="gradient-text">Our Mission</span>
        </motion.h2>
        
        <motion.p 
          className="aboutus-mission-text"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          It's simple. We're putting money back in your pockets so you never overpay for essential services again. Because in today's world, every dollar saved matters.
        </motion.p>
        
        <motion.div 
          className="aboutus-trust-badges"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.9 }}
        >
          <div className="aboutus-trust-badge">
            <Star className="aboutus-trust-icon" size={16} />
            <span>90% Customer Satisfaction</span>
          </div>
          <div className="aboutus-trust-badge">
            <Shield className="aboutus-trust-icon" size={16} />
            <span>Secure Payments</span>
          </div>
          <div className="aboutus-trust-badge">
            <Award className="aboutus-trust-icon" size={16} />
            <span>Trusted Service</span>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

// Advanced interactive info card
const AdvancedInfoCard = ({ icon: Icon, title, content, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.3 });
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const rotateX = (y - centerY) / 30;
    const rotateY = (centerX - x) / 30;
    
    setMousePosition({ rotateX, rotateY });
  };
  
  const springConfig = { damping: 20, stiffness: 300 };
  const rotateXSpring = useSpring(0, springConfig);
  const rotateYSpring = useSpring(0, springConfig);
  
  useEffect(() => {
    rotateXSpring.set(isHovered ? mousePosition.rotateX : 0);
    rotateYSpring.set(isHovered ? mousePosition.rotateY : 0);
  }, [mousePosition, isHovered, rotateXSpring, rotateYSpring]);
  
  return (
    <motion.div 
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.7, delay: 0.2 + index * 0.2 }}
      className="aboutus-info-card-wrapper"
    >
      <motion.div
        ref={cardRef}
        className="aboutus-info-card ultra-glass"
        style={{
          rotateX: rotateXSpring,
          rotateY: rotateYSpring,
          transformPerspective: 1000,
          transformStyle: "preserve-3d"
        }}
        onMouseMove={handleMouseMove}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        whileHover={{
          boxShadow: "0 30px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.15) inset, 0 0 30px rgba(0, 209, 224, 0.15) inset"
        }}
      >
        <div className="aboutus-card-shine"></div>
        <div className="aboutus-card-noise"></div>
        
        <div className="aboutus-info-content-wrapper">
          <motion.div 
            className="aboutus-info-icon-container"
            animate={{
              y: isHovered ? -5 : 0,
              rotate: isHovered ? 5 : 0
            }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 15 
            }}
          >
            <Icon size={48} className="aboutus-info-icon" />
            <motion.div 
              className="aboutus-icon-glow"
              animate={{
                opacity: isHovered ? 0.8 : 0.4,
                scale: isHovered ? 1.3 : 1
              }}
              transition={{ duration: 0.5 }}
            />
          </motion.div>
          
          <motion.h3 
            className="aboutus-info-title"
            animate={{
              transform: isHovered ? "translateZ(20px)" : "translateZ(0px)",
              textShadow: isHovered ? "0 10px 20px rgba(0, 0, 0, 0.3)" : "0 5px 10px rgba(0, 0, 0, 0.1)"
            }}
          >
            {title}
          </motion.h3>
          
          <motion.p 
            className="aboutus-info-content"
            animate={{
              transform: isHovered ? "translateZ(10px)" : "translateZ(0px)"
            }}
          >
            {content}
          </motion.p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Enhanced timeline marker for journey visualization
const EnhancedTimelineMarker = ({ year, event, icon: Icon, highlight, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.3 });
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div 
      ref={ref}
      className="aboutus-timeline-item"
      initial={{ opacity: 0, x: -50 }}
      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
      transition={{ duration: 0.7, delay: 0.2 + index * 0.15 }}
    >
      <motion.div 
        className="aboutus-timeline-connector"
        initial={{ height: 0 }}
        animate={isInView ? { height: "100%" } : { height: 0 }}
        transition={{ duration: 0.7, delay: 0.4 + index * 0.15 }}
      />
      
      <motion.div 
        className="aboutus-timeline-icon-container"
        whileHover={{ scale: 1.1, rotate: 5 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        <Icon size={24} className="aboutus-timeline-icon" />
        <motion.div 
          className="aboutus-timeline-icon-pulse"
          animate={{
            scale: isHovered ? [1, 1.8, 1] : [1, 1.3, 1],
            opacity: isHovered ? [0.2, 0.6, 0.2] : [0.2, 0.4, 0.2]
          }}
          transition={{
            duration: isHovered ? 0.8 : 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </motion.div>
      
      <motion.div 
        className="aboutus-timeline-content ultra-glass-light"
        whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)" }}
      >
        <motion.div 
          className="aboutus-timeline-year"
          animate={{
            scale: isHovered ? 1.1 : 1,
            color: isHovered ? "var(--aboutus-accent-color)" : "var(--aboutus-text-color)"
          }}
          transition={{ duration: 0.3 }}
        >
          {year}
        </motion.div>
        
        <motion.p 
          className="aboutus-timeline-event"
          animate={{
            opacity: isHovered ? 1 : 0.9
          }}
        >
          {event}
        </motion.p>
        
        {highlight && (
          <motion.div 
            className="aboutus-timeline-highlight ultra-glass-xlight"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.6 + index * 0.15 }}
          >
            <span className="aboutus-highlight-text">{highlight}</span>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

// Advanced, interactive journey timeline
const EnhancedJourneyTimeline = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.2], [0.5, 1]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [0.95, 1]);
  
  const milestones = [
    {
      year: "2020",
      event: "From idea to reality—Circledin launches during global economic uncertainty.",
      icon: Rocket
    },
    {
      year: "2021",
      event: "Expanded and added multiple carriers and welcomed our first wave of community members.",
      icon: Users,
      highlight: "Growing Community"
    },
    {
      year: "2022",
      event: "Launched our web platform and celebrated our first major savings milestone.",
      icon: DollarSign,
      highlight: "Major Savings Milestone"
    },
    {
      year: "2023",
      event: "Selected into Founder's University, a prestigious pre-accelerator program founded by Jason Calacanis. Refining our vision and improving user experience.",
      icon: Trophy,
      highlight: "Enhanced User Experience"
    },
    {
      year: "And Beyond",
      event: "Expansion year—introducing new AI features and bringing savings to more people than ever!",
      icon: Rocket
    }
  ];
  
  return (
    <motion.section 
      ref={ref}
      className="aboutus-journey-section"
      style={{
        opacity,
        scale
      }}
    >
      <motion.h2 
        className="aboutus-section-title journey-title"
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.7 }}
      >
        <span className="gradient-text">Our Journey</span>
      </motion.h2>
      
      <div className="aboutus-timeline-container">
        {milestones.map((milestone, index) => (
          <EnhancedTimelineMarker
            key={index}
            {...milestone}
            index={index}
          />
        ))}
      </div>
    </motion.section>
  );
};

// Advanced Promise Cards with 3D hover effects
const PromiseCard = ({ icon: Icon, description, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.3 });
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div 
      ref={ref}
      className="aboutus-promise-card-wrapper"
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, delay: 0.3 + index * 0.2 }}
    >
      <motion.div 
        className="aboutus-promise-card ultra-glass-medium"
        whileHover={{ 
          y: -10, 
          boxShadow: "0 30px 60px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.15) inset" 
        }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        <div className="aboutus-promise-content-wrapper">
          <motion.div 
            className="aboutus-promise-icon-container"
            animate={{
              rotate: isHovered ? 10 : 0,
              scale: isHovered ? 1.1 : 1,
            }}
            transition={{ type: "spring", stiffness: 400, damping: 15 }}
          >
            <Icon size={40} className="aboutus-promise-icon" />
            <motion.div 
              className="aboutus-icon-glow"
              animate={{
                opacity: isHovered ? 0.8 : 0.4,
                scale: isHovered ? 1.5 : 1
              }}
              transition={{ duration: 0.5 }}
            />
          </motion.div>
          
          <motion.p 
            className="aboutus-promise-text"
            animate={{
              y: isHovered ? -5 : 0,
              scale: isHovered ? 1.03 : 1
            }}
            transition={{ duration: 0.3 }}
          >
            {description}
          </motion.p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Scroll-driven sticky explore indicator
const ScrollExploreIndicator = () => {
  const [isVisible, setIsVisible] = useState(true);
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      
      if (scrollPosition > windowHeight * 0.25) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <motion.div 
      className="aboutus-scroll-indicator"
      initial={{ opacity: 1, y: 0 }}
      animate={{ 
        opacity: isVisible ? 1 : 0,
        y: isVisible ? 0 : 20
      }}
      transition={{ duration: 0.5 }}
    >
      <motion.div 
        className="aboutus-scroll-icon"
        animate={{
          y: [0, 10, 0]
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          repeatType: "loop"
        }}
      >
        <ChevronDown size={24} />
      </motion.div>
      <span className="aboutus-scroll-text">Scroll to explore</span>
    </motion.div>
  );
};

// Enhanced Page Title with Character Animation and increased prominence
const EnhancedPageTitle = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false });
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div className="aboutus-title-container">
      <motion.h1 
        ref={ref}
        className="aboutus-page-title"
        initial={{ opacity: 0, y: -30 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30 }}
        transition={{ duration: 0.8 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        ABOUT CIRCLEDIN
      </motion.h1>
      <motion.div 
        className="aboutus-title-underline"
        initial={{ width: 0 }}
        animate={{ width: isHovered ? "100%" : "50%" }}
        transition={{ duration: 0.5 }}
      />
      <motion.p
        className="aboutus-title-trust-message"
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        Trusted by a growing community • Worry-free experience • Life-changing savings
      </motion.p>
    </motion.div>
  );
};

// Enhanced "Why We Started" Section
const EnhancedWhyWeStarted = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.3 });
  
  return (
    <motion.section 
      ref={ref}
      className="aboutus-why-section"
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      <div className="aboutus-why-background ultra-glass">
        <div className="aboutus-why-shine"></div>
        <div className="aboutus-why-noise"></div>
      </div>
      
      <div className="aboutus-why-content">
        <motion.h2 
          className="aboutus-section-title why-title"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <span className="gradient-text">Why Did We Start Circledin?</span>
        </motion.h2>
        
        <motion.p 
          className="aboutus-why-text"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          Circledin was created during the pandemic where we wanted to help people save money. From taking hundreds of calls to understand customer painpoints during a difficult time, it was clear that members needed ways to help them save. We found that Uber drivers, DoorDash, etc. were hit hardest during the pandemic and most of the time had 2 cell phone devices that they were paying full price for. We saw an opportunity to create something meaningful—a platform that leverages the power of community to provide premium mobile services at prices everyone can afford without sacrificing quality. What began as a Reddit discussion has evolved into a movement changing how people think about their mobile plans.
        </motion.p>
        
        <motion.div
          className="aboutus-why-tagline"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5, delay: 0.9 }}
        >
          <span className="aboutus-tagline-text">Saving you money during challenging times</span>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Main AboutUs Component
const AboutUs = () => {
  const promiseItems = [
    {
      icon: Award,
      description: "A commitment to giving back—supporting causes that matter to our community"
    },
    {
      icon: Heart,
      description: "Continuously innovating our platform based on your feedback and needs"
    },
    {
      icon: Users,
      description: "An unwavering focus on customer happiness—because your experience matters as much as your savings"
    }
  ];
  
  const infoCards = [
    {
      icon: Users,
      title: "Group Together, Save Together",
      content: "Welcome to Circledin—where community meets smart savings. We've reimagined mobile plans by bringing people together in Circles, creating a win-win for everyone involved."
    },
    {
      icon: PiggyBank,
      title: "How We Work",
      content: "Think of us as matchmakers for mobile savings. We connect individual subscribers to family plans, just like carpooling but for your phone bill. The result? Hundreds of dollars back in your pocket every year without changing your service."
    }
  ];
  
  return (
    <main className="aboutus-main">
      {/* Background elements */}
      <div className="aboutus-noise-overlay"></div>
      <OrganicBlobs />
      <EnhancedParticles />
      
      {/* Color overlay gradients */}
      <div className="aboutus-gradient-overlay gradient-1"></div>
      <div className="aboutus-gradient-overlay gradient-2"></div>
      
      <div className="aboutus-container">
        {/* Page Title */}
        <EnhancedPageTitle />
        
        {/* Mission Statement */}
        <EnhancedMissionStatement />
        
        {/* Info Cards */}
        <section className="aboutus-info-section">
          {infoCards.map((card, index) => (
            <AdvancedInfoCard 
              key={index}
              icon={card.icon}
              title={card.title}
              content={card.content}
              index={index}
            />
          ))}
        </section>
        
        {/* Why We Started */}
        <EnhancedWhyWeStarted />
        
        {/* Journey Timeline */}
        <EnhancedJourneyTimeline />
        
        {/* Our Promise */}
        <motion.section 
          className="aboutus-promise-section"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <motion.h2 
            className="aboutus-section-title promise-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <span className="gradient-text">Our Promise</span>
          </motion.h2>
          
          <div className="aboutus-promise-grid">
            {promiseItems.map((item, index) => (
              <PromiseCard 
                key={index}
                icon={item.icon}
                description={item.description}
                index={index}
              />
            ))}
          </div>
        </motion.section>
        
        {/* Scroll Indicator */}
        <ScrollExploreIndicator />
      </div>
    </main>
  );
};

export default AboutUs;